import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { Modal, Button, Form, Alert, Spinner, Row, Col } from 'react-bootstrap';
import { batchStore } from '../stores/BatchStore';
const BatchDialog = observer(() => {
    const [formData, setFormData] = useState({
        name: '',
        startTime: '',
        duration: ''
    });
    const [errors, setErrors] = useState({});
    useEffect(() => {
        if (batchStore.isEditing && batchStore.selectedBatch) {
            setFormData({
                name: batchStore.selectedBatch.name,
                startTime: batchStore.selectedBatch.startTime,
                duration: batchStore.selectedBatch.duration?.toString() || ''
            });
        }
        else {
            setFormData({
                name: '',
                startTime: '',
                duration: ''
            });
        }
        setErrors({});
    }, [batchStore.isDialogOpen, batchStore.isEditing, batchStore.selectedBatch]);
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        // Clear error when user starts typing
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };
    const validateForm = () => {
        const newErrors = {};
        if (!formData.name.trim()) {
            newErrors.name = 'Batch name is required';
        }
        else if (formData.name.trim().length < 2) {
            newErrors.name = 'Batch name must be at least 2 characters long';
        }
        else if (formData.name.trim().length > 100) {
            newErrors.name = 'Batch name cannot exceed 100 characters';
        }
        if (!formData.startTime.trim()) {
            newErrors.startTime = 'Start time is required';
        }
        else if (!/^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/.test(formData.startTime)) {
            newErrors.startTime = 'Start time must be in HH:MM format (e.g., 09:30)';
        }
        if (formData.duration && formData.duration.trim() !== '') {
            const duration = parseInt(formData.duration);
            if (isNaN(duration) || duration < 15) {
                newErrors.duration = 'Duration must be at least 15 minutes';
            }
            else if (duration > 480) {
                newErrors.duration = 'Duration cannot exceed 8 hours (480 minutes)';
            }
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) {
            return;
        }
        try {
            const batchData = {
                name: formData.name.trim(),
                startTime: formData.startTime.trim(),
                duration: formData.duration ? parseInt(formData.duration) : undefined
            };
            if (batchStore.isEditing && batchStore.selectedBatch) {
                await batchStore.updateBatch(batchStore.selectedBatch.id, batchData);
            }
            else {
                await batchStore.createBatch(batchData);
            }
            batchStore.closeDialog();
        }
        catch (error) {
            // Error is handled by the store
        }
    };
    const handleClose = () => {
        batchStore.closeDialog();
    };
    return (_jsxs(Modal, { show: batchStore.isDialogOpen, onHide: handleClose, size: "lg", children: [_jsx(Modal.Header, { closeButton: true, children: _jsx(Modal.Title, { children: batchStore.isEditing ? 'Edit Batch' : 'Add New Batch' }) }), _jsxs(Form, { onSubmit: handleSubmit, children: [_jsxs(Modal.Body, { children: [batchStore.hasError && (_jsx(Alert, { variant: "danger", className: "mb-3", children: batchStore.error })), _jsxs(Row, { children: [_jsx(Col, { md: 6, children: _jsxs(Form.Group, { className: "mb-3", children: [_jsx(Form.Label, { children: "Batch Name *" }), _jsx(Form.Control, { type: "text", name: "name", value: formData.name, onChange: handleInputChange, isInvalid: !!errors.name, placeholder: "e.g., Morning Batch, Evening Batch", disabled: batchStore.isLoading }), _jsx(Form.Control.Feedback, { type: "invalid", children: errors.name })] }) }), _jsx(Col, { md: 6, children: _jsxs(Form.Group, { className: "mb-3", children: [_jsx(Form.Label, { children: "Start Time *" }), _jsx(Form.Control, { type: "time", name: "startTime", value: formData.startTime, onChange: handleInputChange, isInvalid: !!errors.startTime, disabled: batchStore.isLoading }), _jsx(Form.Control.Feedback, { type: "invalid", children: errors.startTime })] }) })] }), _jsx(Row, { children: _jsx(Col, { md: 6, children: _jsxs(Form.Group, { className: "mb-3", children: [_jsx(Form.Label, { children: "Duration (minutes)" }), _jsx(Form.Control, { type: "number", name: "duration", value: formData.duration, onChange: handleInputChange, isInvalid: !!errors.duration, placeholder: "e.g., 120 (for 2 hours)", min: "15", max: "480", disabled: batchStore.isLoading }), _jsx(Form.Text, { className: "text-muted", children: "Optional. Duration in minutes (15-480)" }), _jsx(Form.Control.Feedback, { type: "invalid", children: errors.duration })] }) }) })] }), _jsxs(Modal.Footer, { children: [_jsx(Button, { variant: "secondary", onClick: handleClose, disabled: batchStore.isLoading, className: "btn-touch w-mobile-100", children: "Cancel" }), _jsxs(Button, { variant: "primary", type: "submit", disabled: batchStore.isLoading, className: "btn-touch w-mobile-100", children: [batchStore.isLoading && (_jsx(Spinner, { animation: "border", size: "sm", className: "me-2" })), batchStore.isEditing ? 'Update' : 'Create'] })] })] })] }));
});
export default BatchDialog;
